import { Form } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useEffect } from "react";
import { FormModal } from "@/shared/components/FormModal";
import { useCreatePlantContract, useUpdatePlantContract } from "../hooks/usePlantContracts";
import { usePlantContractStore } from "../store/plant-contract.store";
import type { CreatePlantContractRequest } from "../types";
import { PlantContractForm } from "./PlantContractForm";

// DatePicker fields hold Dayjs in the form; converted to ISO strings on submit.
type PlantContractFormValues = Omit<CreatePlantContractRequest, "startDate" | "endDate"> & {
	startDate?: Dayjs;
	endDate?: Dayjs;
};

interface PlantContractFormModalProps {
	organizationId: string;
}

export function PlantContractFormModal({ organizationId }: PlantContractFormModalProps) {
	const [form] = Form.useForm<PlantContractFormValues>();
	const { modalOpen, editingItem, defaultOrganizationId, closeModal } = usePlantContractStore();

	const orgId = editingItem?.organizationId ?? defaultOrganizationId ?? organizationId;

	const { mutate: create, isPending: isCreating } = useCreatePlantContract();
	const { mutate: update, isPending: isUpdating } = useUpdatePlantContract();

	const close = () => {
		form.resetFields();
		closeModal();
	};

	useEffect(() => {
		if (modalOpen) {
			if (editingItem) {
				form.setFieldsValue({
					organizationId: editingItem.organizationId,
					plantId: editingItem.plantId,
					supplierName: editingItem.supplierName,
					contractNumber: editingItem.contractNumber ?? undefined,
					price: editingItem.price ?? undefined,
					quantity: editingItem.quantity ?? undefined,
					startDate: editingItem.startDate ? dayjs(editingItem.startDate) : undefined,
					endDate: editingItem.endDate ? dayjs(editingItem.endDate) : undefined,
				});
			} else {
				form.resetFields();
				form.setFieldsValue({ organizationId: orgId ?? undefined });
			}
		}
	}, [modalOpen, editingItem, orgId, form]);

	const onFinish = (values: PlantContractFormValues) => {
		const startDate = values.startDate ? values.startDate.toISOString() : undefined;
		const endDate = values.endDate ? values.endDate.toISOString() : undefined;
		const price =
			values.price === undefined || values.price === null ? undefined : String(values.price);

		if (editingItem) {
			update(
				{
					id: editingItem.id,
					data: {
						plantId: values.plantId,
						supplierName: values.supplierName,
						contractNumber: values.contractNumber ?? null,
						price: price ?? null,
						quantity: values.quantity ?? null,
						startDate: startDate ?? null,
						endDate: endDate ?? null,
					},
				},
				{ onSuccess: close }
			);
		} else {
			const payload: CreatePlantContractRequest = {
				organizationId: values.organizationId ?? organizationId,
				plantId: values.plantId,
				supplierName: values.supplierName,
				contractNumber: values.contractNumber,
				price,
				quantity: values.quantity,
				startDate,
				endDate,
			};
			create(payload, { onSuccess: close });
		}
	};

	return (
		<FormModal
			open={modalOpen}
			editing={!!editingItem}
			title="Yangi shartnoma"
			editTitle="Shartnomani tahrirlash"
			onCancel={close}
			onOk={form.submit}
			confirmLoading={isCreating || isUpdating}
			width={720}
		>
			<Form form={form} layout="vertical" requiredMark={false} onFinish={onFinish}>
				<PlantContractForm organizationId={orgId ?? undefined} />
			</Form>
		</FormModal>
	);
}
